import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup, Form } from 'react-bootstrap';
import { formatarCNPJ, formatarCNPJInput } from '@spark/utils/formatters';
import { apenasDigitos, mascararRaiz } from '../utils/janelaDescarga';

const TIPOS = [
  { valor: 'RAIZ', texto: 'Raiz do CNPJ (grupo)' },
  { valor: 'CNPJ', texto: 'CNPJ individual' }
];

const Status = ({ children, variante }) => (
  <div className={`small mt-2 ${variante ? `text-${variante}` : 'text-muted'}`}>{children}</div>
);

Status.propTypes = {
  children: PropTypes.node.isRequired,
  variante: PropTypes.string
};

Status.defaultProps = {
  variante: null
};

const ExcecoesRaiz = ({ destino }) => {
  const [aberto, setAberto] = useState(false);
  const total = destino.quantidadeCnpjs ?? 0;
  const proprias = destino.excecoes?.length ?? 0;

  return (
    <>
      <Status variante="success">
        {`${total} CNPJs com esta raiz · ${Math.max(total - proprias, 0)} seguem esta janela · ${proprias} com janela própria`}
      </Status>
      {proprias > 0 ? (
        <>
          <Button
            variant="link"
            size="sm"
            className="p-0"
            aria-expanded={aberto}
            onClick={() => setAberto((atual) => !atual)}
          >
            {`Ver ${proprias} CNPJs com janela própria`}
          </Button>
          {aberto ? (
            <ul className="list-unstyled small mb-0 mt-1">
              {destino.excecoes.map((excecao) => (
                <li key={excecao.id} className="d-flex flex-wrap gap-2">
                  <strong>{excecao.nome}</strong>
                  <span>{formatarCNPJ(excecao.cnpj)}</span>
                  <span className="text-muted">{excecao.resumo}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </>
  );
};

ExcecoesRaiz.propTypes = {
  destino: PropTypes.shape({
    quantidadeCnpjs: PropTypes.number,
    excecoes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      nome: PropTypes.string,
      cnpj: PropTypes.string,
      resumo: PropTypes.string
    }))
  }).isRequired
};

const StatusDestino = ({ rascunho, destino, carregando, erro }) => {
  const porCnpj = rascunho.tipo === 'CNPJ';
  const digitos = porCnpj ? apenasDigitos(rascunho.cnpj, 14) : apenasDigitos(rascunho.cnpjRaiz, 8);
  const raiz = mascararRaiz(digitos);

  if (digitos.length !== (porCnpj ? 14 : 8)) {
    return <Status>{porCnpj ? 'Digite o CNPJ completo.' : 'Digite os 8 dígitos da raiz do CNPJ.'}</Status>;
  }
  if (carregando) return <Status>Consultando destino...</Status>;
  if (erro) return <Status variante="danger">Não foi possível consultar o destino.</Status>;
  if (!destino) return null;

  const duplicada = destino.duplicada ? (
    <Status variante="danger">{`Já existe a janela "${destino.duplicada.nome}" para este destino.`}</Status>
  ) : null;

  if (!destino.encontrado) {
    return (
      <>
        <Status variante="warning">
          {porCnpj ? 'CNPJ não encontrado nas lojas cadastradas.' : `Nenhuma loja cadastrada com a raiz ${raiz}.`}
        </Status>
        {duplicada}
      </>
    );
  }

  if (porCnpj) {
    return (
      <>
        <Status variante="success">{`Loja ${destino.loja?.codigo ?? '-'} · raiz ${raiz}`}</Status>
        <Status>{`Esta janela substitui a da raiz ${raiz} só para este CNPJ.`}</Status>
        {duplicada}
      </>
    );
  }

  return (
    <>
      <ExcecoesRaiz destino={destino} />
      {duplicada}
    </>
  );
};

StatusDestino.propTypes = {
  rascunho: PropTypes.object.isRequired,
  destino: PropTypes.object,
  carregando: PropTypes.bool,
  erro: PropTypes.bool
};

StatusDestino.defaultProps = {
  destino: null,
  carregando: false,
  erro: false
};

const JanelaDestinoSection = ({ rascunho, destino, carregando, erro, onChange }) => {
  const porCnpj = rascunho.tipo === 'CNPJ';

  return (
    <section className="mb-4">
      <h6 className="mb-2">Aplica-se a</h6>
      <ButtonGroup size="sm" className="mb-3">
        {TIPOS.map(({ valor, texto }) => (
          <Button
            key={valor}
            variant={rascunho.tipo === valor ? 'primary' : 'outline-primary'}
            aria-pressed={rascunho.tipo === valor}
            onClick={() => onChange({ tipo: valor })}
          >
            {texto}
          </Button>
        ))}
      </ButtonGroup>

      {porCnpj ? (
        <Form.Group controlId="janela-destino-cnpj" style={{ maxWidth: 320 }}>
          <Form.Label>CNPJ</Form.Label>
          <Form.Control
            inputMode="numeric"
            placeholder="00.000.000/0000-00"
            value={formatarCNPJInput(rascunho.cnpj ?? '')}
            onChange={(event) => onChange({ cnpj: apenasDigitos(event.target.value, 14) })}
          />
        </Form.Group>
      ) : (
        <Form.Group controlId="janela-destino-raiz" style={{ maxWidth: 320 }}>
          <Form.Label>Raiz do CNPJ</Form.Label>
          <Form.Control
            inputMode="numeric"
            placeholder="00.000.000"
            value={mascararRaiz(rascunho.cnpjRaiz)}
            onChange={(event) => onChange({ cnpjRaiz: apenasDigitos(event.target.value, 8) })}
          />
          <Form.Text muted>Ex.: 79.379.491/0023-99 → 79.379.491</Form.Text>
        </Form.Group>
      )}

      <StatusDestino rascunho={rascunho} destino={destino} carregando={carregando} erro={erro} />
    </section>
  );
};

JanelaDestinoSection.propTypes = {
  rascunho: PropTypes.shape({
    tipo: PropTypes.oneOf(['RAIZ', 'CNPJ']).isRequired,
    cnpjRaiz: PropTypes.string,
    cnpj: PropTypes.string
  }).isRequired,
  destino: PropTypes.object,
  carregando: PropTypes.bool,
  erro: PropTypes.bool,
  onChange: PropTypes.func.isRequired
};

JanelaDestinoSection.defaultProps = {
  destino: null,
  carregando: false,
  erro: false
};

export default JanelaDestinoSection;
